<template>
  <el-dialog
    title="复制节点"
    :visible="show"
    width="700px"
    append-to-body
    @close="handleClose"
  >
    <el-form label-position="top" style="margin-bottom: 12px;">
      <el-form-item label="节点标题" style="margin-bottom: 0;">
        <el-input v-model="title" placeholder="请输入节点标题" clearable />
      </el-form-item>
    </el-form>

    <div class="dialog-body">
      <!-- Left panel: chapter list -->
      <div class="chapter-panel">
        <div class="panel-header">目标章节</div>
        <div
          v-for="chapter in chapters"
          :key="chapter.id"
          class="chapter-item"
          :class="{ active: selectedChapterId === chapter.id }"
          @click="selectChapter(chapter.id)"
        >
          {{ chapter.title }}
        </div>
      </div>

      <!-- Right panel: node list -->
      <div class="node-panel">
        <div class="panel-header">插入到哪个节点之后</div>
        <div class="node-list">
          <el-radio-group v-model="selectedAfterNodeId">
            <div class="node-row">
              <el-radio :label="0">
                <span class="node-label">（插入到末尾）</span>
              </el-radio>
            </div>
            <div
              v-for="item in chapterNodes"
              :key="item.id"
              class="node-row"
            >
              <el-radio :label="item.id">
                <span class="node-label">
                  <el-tag size="mini" :type="item.node_type === 'ai' ? 'warning' : 'info'" style="margin-right: 4px;">
                    {{ item.node_type === 'ai' ? 'AI' : '固定' }}
                  </el-tag>
                  #{{ item.id }} {{ item.title }}
                </span>
              </el-radio>
            </div>
            <div v-if="chapterNodes.length === 0" class="empty-tip">该章节暂无节点</div>
          </el-radio-group>
        </div>
      </div>
    </div>

    <span slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm" :loading="loading">确定</el-button>
    </span>
  </el-dialog>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'
import { GameNode, getNodes } from '@/api/node'
import { Chapter } from '@/api/chapter'

@Component
export default class CopyNodeDialog extends Vue {
  @Prop({ default: false }) readonly show!: boolean
  @Prop({ default: () => [] }) readonly chapters!: Chapter[]
  @Prop({ default: null }) readonly sourceNode!: GameNode | null
  @Prop({ default: 0 }) readonly gameId!: number
  /** 当前页面已加载的节点（当前章节） */
  @Prop({ default: () => [] }) readonly currentChapterNodes!: GameNode[]

  title = ''
  selectedChapterId: number | null = null
  selectedAfterNodeId: number | null = 0 // 0 = 插入到末尾
  chapterNodes: GameNode[] = []
  loading = false

  // 缓存已加载的章节节点
  nodesCache: Record<number, GameNode[]> = {}

  @Watch('show')
  onShowChange(val: boolean) {
    if (val) {
      this.initSelection()
    }
  }

  initSelection() {
    this.nodesCache = {}
    this.selectedAfterNodeId = 0
    this.title = this.sourceNode?.title ? this.sourceNode.title + ' (副本)' : ''

    // 默认选中源节点所在章节
    if (this.sourceNode?.game_chapter_id) {
      this.selectedChapterId = this.sourceNode.game_chapter_id
      // 当前章节的节点可以直接用已有数据
      this.nodesCache[this.sourceNode.game_chapter_id] = this.currentChapterNodes
      this.chapterNodes = this.currentChapterNodes
    } else if (this.chapters.length > 0) {
      this.selectedChapterId = this.chapters[0].id
      this.selectChapter(this.chapters[0].id)
    }
  }

  async selectChapter(chapterId: number) {
    this.selectedChapterId = chapterId
    this.selectedAfterNodeId = 0

    if (this.nodesCache[chapterId]) {
      this.chapterNodes = this.nodesCache[chapterId]
      return
    }

    try {
      const res = await getNodes(this.gameId, chapterId)
      const nodes: GameNode[] = (res.data || [])
      this.nodesCache[chapterId] = nodes
      // 确保切换后仍是同一章节
      if (this.selectedChapterId === chapterId) {
        this.chapterNodes = nodes
      }
    } catch (e) {
      this.chapterNodes = []
    }
  }

  handleClose() {
    this.$emit('update:show', false)
  }

  handleConfirm() {
    this.$emit('confirm', {
      title: this.title,
      chapterId: this.selectedChapterId,
      afterNodeId: this.selectedAfterNodeId, // 0 = 末尾
      chapterNodes: this.chapterNodes
    })
  }
}
</script>

<style scoped lang="scss">
.dialog-body {
  display: flex;
  min-height: 400px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.chapter-panel {
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid #e4e7ed;
  overflow-y: auto;
  background: #fafafa;
}

.node-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  border-bottom: 1px solid #e4e7ed;
  background: #f5f7fa;
}

.chapter-item {
  padding: 10px 14px;
  font-size: 13px;
  color: #303133;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;

  &:hover {
    background: #ecf5ff;
    color: #409eff;
  }

  &.active {
    background: #ecf5ff;
    color: #409eff;
    font-weight: 600;
  }
}

.node-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.node-row {
  padding: 6px 14px;
  transition: background 0.1s;

  &:hover {
    background: #f5f7fa;
  }

  .el-radio {
    width: 100%;
    display: flex;
    align-items: center;
    white-space: normal;
    word-break: break-all;
  }
}

.node-label {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
}

.empty-tip {
  padding: 10px 14px;
  font-size: 12px;
  color: #c0c4cc;
}
</style>
